if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.refreshFormElements();
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.addEventListener('click', (event) => {
          if (!event.target.closest('[type="submit"]')) return;
          this.refreshFormElements();
        });

        this.formObserver = new MutationObserver(() => this.refreshFormElements());
        this.formObserver.observe(this, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.formObserver?.disconnect();
      }

      refreshFormElements(form = this.querySelector('form')) {
        const submitButton = form?.querySelector('[type="submit"]');
        if (!form || !submitButton) return false;

        this.form = form;
        this.submitButton = submitButton;
        this.submitButtonText = submitButton.querySelector('span');
        return true;
      }

      restoreInteractivity() {
        if (!this.refreshFormElements()) return;

        // Gift-card recipient validation intentionally controls this wrapper.
        if (this.dataset.hideErrors !== 'true') {
          const wrapper = this.closest('.main-product-buy-buttons');
          wrapper?.classList.remove('disabled');
          wrapper?.removeAttribute('disabled');
          wrapper?.style.removeProperty('pointer-events');
        }

        this.style.removeProperty('pointer-events');
        this.form.style.removeProperty('pointer-events');
        this.submitButton.style.removeProperty('pointer-events');
      }

      resetSubmitButton() {
        if (!this.refreshFormElements()) return;
        this.submitting = false;
        this.error = false;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      readCartResponse(response) {
        return response.text().then((text) => {
          cartDebug('add: response', {
            status: response.status,
            contentType: response.headers.get('content-type'),
            start: text.slice(0, 120),
          });
          try {
            return JSON.parse(text);
          } catch (error) {
            return null;
          }
        });
      }

      withCartSections(cartState) {
        if (!this.cart || cartState.sections?.['cart-drawer']) return Promise.resolve(cartState);

        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id).join(',');
        return fetch(`${routes.cart_url}?sections=${sectionIds}`)
          .then((response) => response.json())
          .then((sections) => {
            cartState.sections = sections;
            return cartState;
          })
          .catch(() => cartState);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.refreshFormElements(evt.target)) return;
        if (this.submitting) return;

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        this.handleErrorMessage();
        this.submitting = true;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => this.readCartResponse(response))
          .then((response) => {
            if (response) return response;

            return fetch(`${routes.cart_url}.js`)
              .then((cartResponse) => cartResponse.json())
              .then((cart) => {
                const alreadyAdded = cart.items?.some((item) => String(item.variant_id) === String(variantId));
                if (alreadyAdded) return cart;

                const retry = fetchConfig('javascript');
                retry.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete retry.headers['Content-Type'];
                retry.body = new FormData(this.form);
                return fetch(`${routes.cart_add_url}`, retry).then((retryResponse) => this.readCartResponse(retryResponse));
              });
          })
          .then((response) => {
            if (!response) {
              this.handleErrorMessage(window.cartStrings.error);
              this.error = true;
              this.cart?.close?.();
              return;
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            return this.withCartSections(response).then((cartState) => {
              this.error = false;
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: cartState,
              });

              try {
                this.cart.renderContents(cartState);
              } catch (error) {
                console.error(error);
                this.cart.open?.(this.submitButton);
              }
            });
          })
          .catch((error) => {
            console.error(error);
            this.handleErrorMessage(window.cartStrings.error);
            this.error = true;
            this.cart?.close?.();
          })
          .finally(() => {
            this.resetSubmitButton();
          });
      }

      submitFromButton(event) {
        if (!this.refreshFormElements() || this.submitButton.disabled) return;

        // Cancelling the button's native activation avoids a second submit.
        // The form handler is invoked directly because third-party scripts can
        // cancel/stop the click before the browser performs its submit action.
        event.preventDefault();
        this.onSubmitHandler({
          preventDefault() {},
          target: this.form,
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.refreshFormElements()) return;
        this.restoreInteractivity();
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );

  window.addEventListener(
    'click',
    (event) => {
      const submitButton = event.target.closest('product-form .product-form__submit[type="submit"]');
      if (!submitButton) return;

      const productForm = submitButton.closest('product-form');
      productForm?.submitFromButton?.(event);
    },
    true
  );
}
